import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

const DEFAULT_TOOLTIP_GAP = 8;
const VIEWPORT_PADDING = 12;
const BOUNDARY_PADDING = 10;

const SIZE_MAX_WIDTH = {
  default: 128,
  wide: 268,
  menu: 168,
  /** Stretch to boundary width (e.g. tool-prep left panel). */
  panel: 9999,
};

const Tooltip = ({
  children,
  content,
  position = "top",
  size = "default",
  align = "center",
  boundaryRef = null,
  /** When set with position="bottom"|"top", tip uses this edge instead of the trigger. */
  edgeRef = null,
  /** Distance from trigger (or edgeRef) in px. Default 8. */
  gap = DEFAULT_TOOLTIP_GAP,
  flip = true,
  /** Stretch trigger to full parent width (menus / form fields). */
  block = false,
  /** Stretch tip to fill boundaryRef width (minus padding). Requires boundaryRef. */
  fillBoundary = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({
    top: -9999,
    left: -9999,
    maxWidth: undefined,
    width: undefined,
  });
  const [ready, setReady] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltipEl = tooltipRef.current;
    if (!trigger || !tooltipEl) return;

    const boundary = boundaryRef?.current?.getBoundingClientRect();
    const edge = edgeRef?.current?.getBoundingClientRect();
    const tipGap = Number.isFinite(Number(gap)) ? Number(gap) : DEFAULT_TOOLTIP_GAP;
    const fillWidth =
      fillBoundary && boundary
        ? Math.max(120, boundary.width - BOUNDARY_PADDING * 2)
        : undefined;

    // Apply fill width before measuring height so wrapped tip text is correct.
    if (fillWidth != null) {
      tooltipEl.style.width = `${fillWidth}px`;
      tooltipEl.style.maxWidth = `${fillWidth}px`;
    }

    const { width: tooltipWidth, height: tooltipHeight } =
      tooltipEl.getBoundingClientRect();
    const rect = trigger.getBoundingClientRect();

    let top;
    if (position === "bottom") {
      // Prefer a shared edge (e.g. toolbar bottom) when provided.
      top = (edge ? edge.bottom : rect.bottom) + tipGap;
    } else {
      top = (edge ? edge.top : rect.top) - tooltipHeight - tipGap;
    }

    let left;
    if (fillWidth != null && boundary) {
      left = boundary.left + BOUNDARY_PADDING;
    } else if (align === "start") {
      left = rect.left;
    } else if (align === "end") {
      left = rect.right - tooltipWidth;
    } else {
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    const minLeft = boundary
      ? boundary.left + BOUNDARY_PADDING
      : VIEWPORT_PADDING;
    const maxLeft = boundary
      ? boundary.right - (fillWidth ?? tooltipWidth) - BOUNDARY_PADDING
      : window.innerWidth - tooltipWidth - VIEWPORT_PADDING;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    const minTop = boundary ? boundary.top + BOUNDARY_PADDING : VIEWPORT_PADDING;
    const maxTop = boundary
      ? boundary.bottom - tooltipHeight - BOUNDARY_PADDING
      : window.innerHeight - tooltipHeight - VIEWPORT_PADDING;

    if (position === "top" && top < minTop) {
      if (flip) {
        top = (edge ? edge.bottom : rect.bottom) + tipGap;
      } else {
        top = minTop;
      }
    } else if (
      flip &&
      position === "bottom" &&
      top + tooltipHeight >
        (boundary?.bottom ?? window.innerHeight) - BOUNDARY_PADDING
    ) {
      top = (edge ? edge.top : rect.top) - tooltipHeight - tipGap;
    }

    top = Math.max(minTop, Math.min(top, maxTop));

    const sizeCap = SIZE_MAX_WIDTH[size] ?? SIZE_MAX_WIDTH.default;
    const maxWidth =
      fillWidth != null
        ? fillWidth
        : boundary
          ? Math.min(sizeCap, Math.max(120, boundary.width - BOUNDARY_PADDING * 2))
          : undefined;

    setCoords({ top, left, maxWidth, width: fillWidth });
    setReady(true);
  }, [position, align, boundaryRef, edgeRef, gap, size, flip, fillBoundary]);

  const show = () => {
    if (!content) return;
    setReady(false);
    setVisible(true);
  };

  const hide = () => {
    setVisible(false);
    setReady(false);
  };

  useLayoutEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    const onReflow = () => updatePosition();
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [visible, content, updatePosition]);

  if (!content) {
    return children;
  }

  return (
    <>
      <span
        ref={triggerRef}
        className={`${styles.wrapper}${block ? ` ${styles.wrapperBlock}` : ""}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            className={`${styles.tooltipPortal} ${styles[size] || ""} ${
              fillBoundary ? styles.panel : ""
            } ${ready ? styles.visible : styles.measuring}`}
            style={{
              top: coords.top,
              left: coords.left,
              maxWidth: coords.maxWidth,
              width: coords.width,
            }}
            role="tooltip"
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
